import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Users, Trash2, Loader2, Mail, CheckCircle2, Clock, ShieldCheck, LayoutTemplate, Send, Upload } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { RightsEditorDialog } from "@/components/RightsEditorDialog";
import { RightsTemplatesDialog } from "@/components/RightsTemplatesDialog";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

export default function Investors() {
  const { company, user } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "" });
  const [adding, setAdding] = useState(false);
  const [rightsFor, setRightsFor] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [introFor, setIntroFor] = useState(null);
  const [introNote, setIntroNote] = useState("");
  const [sendingIntro, setSendingIntro] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${company.id}/investors`); setInvestors(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data: created } = await api.post(`/companies/${company.id}/investors`, form);
      toast.success(`${form.name} added - send them an invite to grant portal access`);
      setForm({ name: "", email: "" });
      await load();
      openIntro(created);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add investor"); }
    finally { setAdding(false); }
  };

  const remove = async (iid) => {
    try { await api.delete(`/companies/${company.id}/investors/${iid}`); load(); toast.success("Investor removed"); }
    catch { toast.error("Remove failed"); }
  };

  const openIntro = (inv) => { setIntroFor(inv); setIntroNote(""); };

  const bulkAdd = async () => {
    if (!bulkText.trim()) { toast.error("Paste investor emails first"); return; }
    setBulkLoading(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/investors/bulk`, { text: bulkText });
      const bits = [`${data.added} added`];
      if (data.invited) bits.push(`${data.invited} invited`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      if (data.invalid?.length) bits.push(`${data.invalid.length} unreadable`);
      toast.success(bits.join(" · "));
      if (data.hit_limit) toast.message("You've reached your plan's investor limit - upgrade to add more.");
      setBulkText(""); setBulkOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk add failed"); }
    finally { setBulkLoading(false); }
  };

  const sendIntro = async () => {
    if (!introFor) return;
    setSendingIntro(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/investors/${introFor.id}/intro`, { note: introNote || null });
      if (data.status === "sent") {
        toast.success(`Invite email sent to ${introFor.email}`);
        setIntroFor(null);
      } else if (data.status === "simulated") {
        toast.warning(`Email isn't configured on this environment, so nothing was actually delivered to ${introFor.email}.`, { duration: 7000 });
        setIntroFor(null);
      } else {
        toast.error(`We couldn't deliver the invite to ${introFor.email}. Your sending domain likely isn't verified in production (set SENDER_EMAIL and verify your domain in Resend). Nothing was sent.`, { duration: 9000 });
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send intro email"); }
    finally { setSendingIntro(false); }
  };

  return (
    <AppShell
      title="Investors"
      subtitle="Manage who receives your updates"
      actions={
        <Button variant="outline" className="gap-2" onClick={() => setTemplatesOpen(true)} data-testid="manage-rights-templates">
          <LayoutTemplate className="h-4 w-4" /> Rights templates
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
        <Card className="h-fit rounded-xl border bg-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Add investor</h2>
          </div>
          <form onSubmit={add} className="space-y-3">
            <div>
              <Label htmlFor="iname">Name</Label>
              <Input id="iname" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="investor-name" />
            </div>
            <div>
              <Label htmlFor="iemail">Email</Label>
              <Input id="iemail" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="investor-email" />
            </div>
            <Button type="submit" disabled={adding} className="w-full gap-2" data-testid="investor-add">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add investor
            </Button>
          </form>
          <p className="mt-3 text-xs text-muted-foreground">Adding creates their record - you'll be prompted to email them an invite with a link to your portal. They become active once they sign in with this email.</p>
          <div className="mt-3 border-t border-border pt-3">
            <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setBulkOpen(true)} data-testid="investor-bulk-open">
              <Upload className="h-4 w-4" /> Bulk add investors
            </Button>
          </div>
        </Card>

        <div>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : investors.length === 0 ? (
            <EmptyState icon={Users} title="No investors yet" description="Investors are the people who receive your updates and can open your portal. Adding one takes a minute." steps={["Add a name and email with the form, or bulk-add from a spreadsheet", "Send the invite; investors join the portal free with that email", "Every update you publish reaches them the way you choose"]} secondaryLabel="See how delivery works" secondaryTo="/app/delivery" testId="empty-state-investors" />
          ) : (
            <div className="space-y-2">
              {investors.map((inv) => (
                <Card key={inv.id} className="flex items-center justify-between rounded-xl border bg-card p-4" data-testid={`investor-item-${inv.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-primary">
                      {inv.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{inv.name}</div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{inv.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.has_account ? (
                      <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" />Active</Badge>
                    ) : inv.intro_sent_at ? (
                      <Badge className="border-sky-200 bg-sky-50 text-sky-700" title={`Invite sent ${formatDate(inv.intro_sent_at)}`} data-testid={`investor-invited-${inv.id}`}><Send className="mr-1 h-3 w-3" />Invite sent</Badge>
                    ) : (
                      <Badge className="border-amber-200 bg-amber-100 text-amber-800" data-testid={`investor-not-invited-${inv.id}`}><Clock className="mr-1 h-3 w-3" />Not invited yet</Badge>
                    )}
                    <Button variant={inv.intro_sent_at || inv.has_account ? "outline" : "default"} size="sm" className="gap-1.5" onClick={() => openIntro(inv)} data-testid={`investor-intro-${inv.id}`}>
                      <Send className="h-3.5 w-3.5" /> {inv.intro_sent_at ? "Resend invite" : "Invite now"}
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setRightsFor(inv)} data-testid={`investor-rights-${inv.id}`}>
                      <ShieldCheck className="h-3.5 w-3.5" /> Rights
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(inv.id)} data-testid={`investor-remove-${inv.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {company && (
        <RightsEditorDialog
          open={!!rightsFor}
          onOpenChange={(v) => !v && setRightsFor(null)}
          companyId={company.id}
          investor={rightsFor}
          onSaved={load}
        />
      )}
      {company && (
        <RightsTemplatesDialog
          open={templatesOpen}
          onOpenChange={setTemplatesOpen}
          companyId={company.id}
        />
      )}

      <Dialog open={!!introFor} onOpenChange={(v) => !v && setIntroFor(null)}>
        <DialogContent data-testid="intro-dialog">
          <DialogHeader>
            <DialogTitle>Send onboarding email{introFor ? ` to ${introFor.name}` : ""}</DialogTitle>
            <DialogDescription>
              A personalized welcome that introduces {introFor?.name?.split(" ")[0] || "them"} to {company?.name}'s investor portal and the advantages of TenX - with a link to jump in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="intro-note">Add a personal note (optional)</Label>
            <Textarea
              id="intro-note"
              value={introNote}
              onChange={(e) => setIntroNote(e.target.value)}
              placeholder={`e.g. Great meeting you last week - excited to keep you posted on our progress. - ${(user?.name || "").split(" ")[0]}`}
              rows={4}
              data-testid="intro-note"
            />
            <p className="text-xs text-muted-foreground">Sent to {introFor?.email}. Leave blank to send the standard welcome.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntroFor(null)} data-testid="intro-cancel">Cancel</Button>
            <Button onClick={sendIntro} disabled={sendingIntro} className="gap-2" data-testid="intro-send">
              {sendingIntro ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-investors-dialog">
          <DialogHeader>
            <DialogTitle>Bulk add investors</DialogTitle>
            <DialogDescription>
              Paste one investor per line. We'll read <code className="rounded bg-secondary px-1">Name, email</code>, <code className="rounded bg-secondary px-1">Name &lt;email&gt;</code> or just an email. Duplicates and existing investors are skipped. Everyone new is automatically emailed an invite to your portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              placeholder={"Jordan Lee, jordan@fund.com\nSam Patel <sam@angels.vc>\ntaylor@seedco.com"}
              className="font-mono text-xs"
              data-testid="bulk-investors-text"
            />
            <p className="text-xs text-muted-foreground">Tip: copy a column of emails straight from a spreadsheet - one per line works great.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-investors-cancel">Cancel</Button>
            <Button onClick={bulkAdd} disabled={bulkLoading} className="gap-2" data-testid="bulk-investors-submit">
              {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Add investors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
